import React from 'react';
import heroImg from '../componts/img/home_pic.jpg';
import evChargingImg from '../componts/img/ev-charging.png';
import constructionHeaderImg from '../componts/img/construction-header.jpg';
import portsHeaderImg from '../componts/img/ports-header.jpg';
import emergencyHeaderImg from '../componts/img/emergency-header.jpg';
import ruralHeaderImg from '../componts/img/rural-header.jpg';
import eventsHeaderImg from '../componts/img/events-header.jpg';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './home.css';

function Home() {
    return (
        <>
            {/* ── HERO ── */}
            <section className="hero">
                <div className="hero__content">
                    <span className="hero__eyebrow">Introducing HydroVerra</span>
                    <h1 className="hero__headline">
                        The Fastest Way to Deploy<br />
                        Clean Power Anywhere
                    </h1>
                    <p className="hero__sub">
                        HydroVerra is a self-contained hydrogen power unit that deploys in 2–4 weeks — no grid connection delays,
                        no diesel fumes, no grid bottleneck. Perfect for EV charging hubs, construction sites, ports, rural communities,
                        and emergency response. Zero emissions. Fully autonomous.
                    </p>
                    <div className="hero__ctas">
                        <button className="btn-primary" onClick={() => window.location.href = '/contactus'}>
                            Get Your Quote <i className="bi bi-arrow-right" />
                        </button>
                        <button className="btn-outline" onClick={() => window.location.href = '/ourtechnology/HydroVerra'}>
                            How It Works <i className="bi bi-arrow-right" />
                        </button>
                    </div>
                </div>
                <div className="hero__image">
                    <img src={heroImg} alt="HydroVerra hydrogen energy system" width={800} height={533} loading="lazy" />
                </div>
            </section>

            {/* ── STATS BAR ── */}
            <div className="stats-bar">
                <div className="stat">
                    <span className="stat__value">Zero</span>
                    <span className="stat__label">Carbon Emissions</span>
                </div>
                <div className="stat-divider" />
                <div className="stat">
                    <span className="stat__value">Modular</span>
                    <span className="stat__label">Scalable Design</span>
                </div>
                <div className="stat-divider" />
                <div className="stat">
                    <span className="stat__value">AI</span>
                    <span className="stat__label">Optimised Control</span>
                </div>
                <div className="stat-divider" />
                <div className="stat">
                    <span className="stat__value">Mobile</span>
                    <span className="stat__label">Deploy Anywhere</span>
                </div>
            </div>

            {/* ── HOW IT WORKS ── */}
            <section className="platform-story">
                <div className="platform-story__inner">
                    <span className="section-eyebrow">The Platform</span>
                    <h2 className="section-heading">How HydroVerra Works</h2>
                    <p className="section-sub">
                        From renewable source to zero-emission power — a complete, closed-loop system.
                    </p>

                    <div className="platform-ai-wrap">
                        <div className="platform-ai-badge">
                            <i className="bi bi-cpu-fill" />
                            AI Optimised Control
                        </div>

                        <div className="platform-steps">
                            <div className="platform-step">
                                <div className="platform-step__num">01</div>
                                <div className="platform-step__icon">
                                    <i className="bi bi-lightning-charge-fill" />
                                </div>
                                <h3 className="platform-step__title">Generate</h3>
                                <p className="platform-step__desc">
                                    Renewable sources power our electrolyser, splitting water into clean hydrogen gas.
                                </p>
                            </div>

                            <div className="platform-arrow">
                                <i className="bi bi-arrow-right" />
                            </div>

                            <div className="platform-step">
                                <div className="platform-step__num">02</div>
                                <div className="platform-step__icon">
                                    <i className="bi bi-battery-charging" />
                                </div>
                                <h3 className="platform-step__title">Store</h3>
                                <p className="platform-step__desc">
                                    Compressed hydrogen is held in modular tanks — days or weeks of energy on standby.
                                </p>
                            </div>

                            <div className="platform-arrow">
                                <i className="bi bi-arrow-right" />
                            </div>

                            <div className="platform-step">
                                <div className="platform-step__num">03</div>
                                <div className="platform-step__icon">
                                    <i className="bi bi-truck" />
                                </div>
                                <h3 className="platform-step__title">Deliver</h3>
                                <p className="platform-step__desc">
                                    The mobile HydroVerra unit deploys to any site — off-grid, remote, or temporary.
                                </p>
                            </div>

                            <div className="platform-arrow">
                                <i className="bi bi-arrow-right" />
                            </div>

                            <div className="platform-step">
                                <div className="platform-step__num">04</div>
                                <div className="platform-step__icon">
                                    <i className="bi bi-plug-fill" />
                                </div>
                                <h3 className="platform-step__title">Power</h3>
                                <p className="platform-step__desc">
                                    On-demand clean electricity for EVs, communities, events, or industrial operations.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="platform-story__cta-wrap">
                        <button
                            className="btn-primary btn-primary--light"
                            onClick={() => window.location.href = '/ourtechnology/HydroVerra'}
                        >
                            Explore HydroVerra <i className="bi bi-arrow-right" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ── WHO WE SERVE ── */}
            <section className="serve-section">
                <div className="serve-section__inner">
                    <span className="section-eyebrow section-eyebrow--dark">Use Cases</span>
                    <h2 className="section-heading section-heading--dark">Built for Every Sector</h2>
                    <p className="section-sub section-sub--dark">
                        HydroVerra is already being designed for pilot deployment across these sectors.
                    </p>
                    <div className="serve-grid serve-grid--six">
                        {[
                            { icon: 'bi-ev-station',       title: 'EV Charging',          path: '/sectors/ev-charging', img: evChargingImg, desc: 'Hydrogen-powered charging hubs where the grid cannot support rapid charging.' },
                            { icon: 'bi-cone-striped',     title: 'Construction',        path: '/sectors/construction', img: constructionHeaderImg, desc: 'Off-grid site power replacing diesel generators with zero emissions.' },
                            { icon: 'bi-tsunami',          title: 'Ports, Marine and Vessels', path: '/sectors/ports-maritime', img: portsHeaderImg, desc: 'Shore power, harbour operations, ferry support, and remote island energy supply.' },
                            { icon: 'bi-exclamation-triangle-fill', title: 'Emergency Response', path: '/sectors/emergency', img: emergencyHeaderImg, desc: 'Rapid-deploy clean power for disaster relief, military, and critical emergency operations.' },
                            { icon: 'bi-house-fill',       title: 'Rural Communities',    path: '/sectors/rural-remote', img: ruralHeaderImg, desc: 'Reliable, clean electricity for remote villages and off-grid communities.' },
                            { icon: 'bi-building',         title: 'Events & Industry',    path: '/sectors/events', img: eventsHeaderImg, desc: 'Temporary power for festivals, public events, and industrial sites — diesel-free.' },
                        ].map((item) => (
                            <a href={item.path} className={`serve-card${item.primary ? ' serve-card--primary' : ''}`} key={item.title} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <div className="serve-card__content">
                                    <h3 className="serve-card__title">{item.title}{item.primary && <span className="serve-card__badge">Primary</span>}</h3>
                                    <p className="serve-card__desc">{item.desc} <span className="serve-card__cta">View Sector →</span></p>
                                </div>
                                <div className="serve-card__img-wrap">
                                    <img src={item.img} alt={item.title} className="serve-card__img" />
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── THE PROBLEM WE SOLVE ── */}
            <section className="problem-section">
                <div className="problem-section__left">
                    <span className="section-eyebrow">The Challenge</span>
                    <h2 className="section-heading">The Energy Gap We're Closing</h2>
                    <p className="section-sub" style={{ textAlign: 'left', marginLeft: 0 }}>
                        Millions of sites worldwide are cut off from reliable, clean power.
                        OctoNexus Power was built to solve the three biggest barriers.
                    </p>
                </div>
                <div className="problem-section__right">
                    {[
                        {
                            icon: 'bi-cloud-haze2-fill',
                            stat: '95%',
                            desc: 'of temporary and off-grid power in the UK still relies on diesel generators — a major source of avoidable carbon emissions.',
                        },
                        {
                            icon: 'bi-currency-pound',
                            stat: '£1M+',
                            desc: 'is the typical cost to connect a remote site to the UK national grid — taking years to approve and install.',
                        },
                        {
                            icon: 'bi-geo-alt-fill',
                            stat: '1 in 3',
                            desc: 'construction sites, ports, and rural communities lack access to clean, reliable power where and when they need it.',
                        },
                    ].map((p, i) => (
                        <div className="problem-card" key={i}>
                            <div className="problem-card__icon">
                                <i className={`bi ${p.icon}`} />
                            </div>
                            <div className="problem-card__body">
                                <h3 className="problem-card__stat">{p.stat}</h3>
                                <p className="problem-card__desc">{p.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── COST COMPARISON ── */}
            <section className="comparison-section">
                <div className="comparison-section__inner">
                    <span className="section-eyebrow section-eyebrow--dark">The Business Case</span>
                    <h2 className="section-heading section-heading--dark">HydroVerra vs Grid Connection vs Diesel Generators</h2>
                    <p className="section-sub section-sub--dark">
                        B2B procurement teams compare cost, time, and operational impact. Here's what matters.
                    </p>
                    <div className="comparison-table-wrap">
                        <table className="comparison-table">
                            <thead>
                                <tr>
                                    <th className="comparison-table__header-label">Metric</th>
                                    <th className="comparison-table__header">Grid Connection</th>
                                    <th className="comparison-table__header">Diesel Generator</th>
                                    <th className="comparison-table__header comparison-table__header--primary">HydroVerra</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="comparison-table__label">Upfront Cost</td>
                                    <td>£500k–£1M+</td>
                                    <td>£50k–£150k</td>
                                    <td className="comparison-table__cell--primary">£300k–£500k</td>
                                </tr>
                                <tr>
                                    <td className="comparison-table__label">Deployment Time</td>
                                    <td>18–24 months</td>
                                    <td>2–4 weeks</td>
                                    <td className="comparison-table__cell--primary">2–4 weeks</td>
                                </tr>
                                <tr>
                                    <td className="comparison-table__label">Annual Op Cost</td>
                                    <td>£5k–£20k (grid fees)</td>
                                    <td>£80k–£150k (fuel)</td>
                                    <td className="comparison-table__cell--primary">£15k–£35k (H₂)</td>
                                </tr>
                                <tr>
                                    <td className="comparison-table__label">CO₂ Emissions (annual)</td>
                                    <td>0 (from grid)</td>
                                    <td>200–400 tonnes</td>
                                    <td className="comparison-table__cell--primary">0 (green H₂)</td>
                                </tr>
                                <tr>
                                    <td className="comparison-table__label">DNO Approval Required</td>
                                    <td>Yes (6–12mo)</td>
                                    <td>No</td>
                                    <td className="comparison-table__cell--primary">No</td>
                                </tr>
                                <tr>
                                    <td className="comparison-table__label">Noise Level</td>
                                    <td>N/A</td>
                                    <td>75–85 dB</td>
                                    <td className="comparison-table__cell--primary">&lt;50 dB</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="comparison-section__note">
                        * Costs are UK-based estimates for a 200kW system, 18-month deployment. Actual figures vary by site, region, and power requirement. Contact us for detailed ROI analysis.
                    </p>
                </div>
            </section>

            {/* ── COMPETITIVE ADVANTAGE ── */}
            <section className="advantage-section">
                <div className="advantage-section__inner">
                    <span className="section-eyebrow section-eyebrow--dark">Why HydroVerra</span>
                    <h2 className="section-heading section-heading--dark">Faster Than Grid. Cleaner Than Diesel. More Autonomous Than Batteries.</h2>
                    <p className="section-sub section-sub--dark">
                        Grid connection takes 18–24 months. Diesel runs out and costs £500/day. Batteries need frequent replacement. HydroVerra deploys in weeks and runs for years.
                    </p>
                    <div className="advantage-grid">
                        {[
                            {
                                icon: 'bi-puzzle-fill',
                                title: 'Fully Integrated System',
                                desc: 'Generation, storage, and fuel cell output in one deployable unit — no third-party integration required.',
                            },
                            {
                                icon: 'bi-battery-half',
                                title: 'Hybrid Storage Advantage',
                                desc: 'Combines hydrogen long-duration storage with battery buffers for uninterrupted, optimised power delivery.',
                            },
                            {
                                icon: 'bi-geo-alt-fill',
                                title: 'True Off-Grid Capability',
                                desc: 'No grid connection, no diesel, no compromises. Operates in the most remote and demanding environments.',
                            },
                            {
                                icon: 'bi-cpu-fill',
                                title: 'Intelligent Energy Management',
                                desc: 'AI-driven control continuously optimises energy flow, predicts demand, and reduces waste across all conditions.',
                            },
                        ].map((a) => (
                            <div className="advantage-card" key={a.title}>
                                <div className="advantage-card__icon">
                                    <i className={`bi ${a.icon}`} />
                                </div>
                                <h3 className="advantage-card__title">{a.title}</h3>
                                <p className="advantage-card__desc">{a.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── PARTNERS & SUPPORTERS ── */}
            <section className="partners-bar">
                <p className="partners-bar__label">Backed &amp; Supported By</p>
                <div className="partners-bar__logos">
                    <span className="partner-badge">Business Gateway Aberdeen</span>
                    <span className="partner-badge">University of Aberdeen</span>
                    <span className="partner-badge">Interface</span>
                    <span className="partner-badge">Rolls-Royce Hydrogen Hub</span>
                </div>
            </section>

            {/* ── DEVELOPMENT STATUS ── */}
            <section className="devstatus-section">
                <div className="devstatus-section__inner">
                    <span className="section-eyebrow">Validation</span>
                    <h2 className="section-heading">Where We Are Today</h2>
                    <p className="section-sub">
                        HydroVerra is progressing through a structured development programme — from validated R&amp;D to pilot deployment.
                    </p>
                    <div className="devstatus-track">
                        {[
                            { step: '01', label: 'R&D Completed',         status: 'done',    desc: 'System architecture, electrolyser selection, fuel cell integration and AI control model validated.' },
                            { step: '02', label: 'Supplier Verification', status: 'active',  desc: 'Engaging leading PEMFC and electrolyser suppliers globally. NDAs signed with key technology partners.' },
                            { step: '03', label: 'Certification & Testing', status: 'next', desc: 'Component and system-level certification under UK and EU hydrogen safety standards.' },
                            { step: '04', label: 'Pilot Deployment',       status: 'next',    desc: 'Initial pilots with select partners across EV charging, construction, and marine sectors.' },
                        ].map((s) => (
                            <div className={`devstatus-item devstatus-item--${s.status}`} key={s.step}>
                                <div className="devstatus-item__badge">
                                    {s.status === 'done'
                                        ? <i className="bi bi-check-lg" />
                                        : s.status === 'active'
                                        ? <i className="bi bi-arrow-repeat" />
                                        : <span>{s.step}</span>}
                                </div>
                                <div className="devstatus-item__body">
                                    <h4 className="devstatus-item__label">{s.label}</h4>
                                    <p className="devstatus-item__desc">{s.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── PILOT PROGRAMME CTA ── */}
            <section className="pilots-section">
                <div className="pilots-section__inner">
                    <span className="section-eyebrow section-eyebrow--dark">Early Access</span>
                    <h2 className="section-heading section-heading--dark">Join Our Pilot Programme</h2>
                    <p className="section-sub section-sub--dark">
                        We are actively seeking pilot partners across construction, ports, EV charging, and public sector.
                        Be among the first to deploy HydroVerra at your site — with full support from our team.
                    </p>
                    <div className="platform-story__cta-wrap">
                        <button
                            className="btn-primary"
                            onClick={() => window.location.href = '/contactus'}
                        >
                            Express Interest <i className="bi bi-arrow-right" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ── STAY CONNECTED ── */}
            <section className="news-teaser">
                <div className="news-teaser__inner">
                    <div className="news-teaser__header">
                        <div>
                            <span className="section-eyebrow">Latest</span>
                            <h2 className="section-heading">News &amp; Updates</h2>
                        </div>
                        <a href="/news" className="btn-outline" style={{ whiteSpace: 'nowrap' }}>
                            View All <i className="bi bi-arrow-right" />
                        </a>
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '1.5rem',
                    }}>
                        {[
                            {
                                category: 'Funding News',
                                title: 'Interface Fund Award Accelerates HydroVerra Development',
                                excerpt: 'OctoNexus Power has secured funding through the Interface Fund to advance HydroVerra toward commercial deployment.',
                                date: 'March 2026',
                            },
                            {
                                category: 'Partnerships',
                                title: 'University of Aberdeen Collaboration Strengthens R&D Capabilities',
                                excerpt: 'OctoNexus Power partners with the University of Aberdeen to enhance hydrogen energy system research.',
                                date: 'February 2026',
                            },
                            {
                                category: 'Updates',
                                title: 'Business Gateway Scotland Guides Go-to-Market Strategy',
                                excerpt: 'With guidance from Business Gateway Scotland, OctoNexus Power has refined its commercialisation roadmap.',
                                date: 'January 2026',
                            },
                        ].map((article, idx) => (
                            <div
                                key={idx}
                                onClick={() => window.location.href = '/news'}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: '1.5rem',
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#0fa19a';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 161, 154, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <span style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    color: '#0fa19a',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    marginBottom: '0.5rem',
                                }}>
                                    {article.category}
                                </span>
                                <h3 style={{
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: '#070468',
                                    margin: '0 0 0.75rem',
                                    lineHeight: 1.4,
                                }}>
                                    {article.title}
                                </h3>
                                <p style={{
                                    fontSize: '0.9rem',
                                    color: '#6b7280',
                                    lineHeight: 1.5,
                                    margin: '0 0 1rem',
                                    flex: 1,
                                }}>
                                    {article.excerpt}
                                </p>
                                <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                                    {article.date}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FINAL CTA ── */}
            <section className="final-cta">
                <h2 className="final-cta__heading">Ready to power your project?</h2>
                <p className="final-cta__sub">
                    We are seeking pilot partners, early adopters, and strategic collaborators across EV charging, construction, marine, and emergency response.
                </p>
                <div className="hero__ctas">
                    <button className="btn-primary" onClick={() => window.location.href = '/contactus'}>
                        Request a Demo
                    </button>
                    <button className="btn-outline" onClick={() => window.location.href = '/contactus'}>
                        Partner With Us
                    </button>
                </div>
            </section>
        </>
    );
}

export default Home;
